// Drumkit API + portal base resolution from mailbox / authenticated email.
import { getEmailRoutingDefinition } from '@/config/emailRouting';
import type { AxiosInstance } from 'axios';

import DRUMKIT_API_URL from '@constants/DrumkitApiUrl';
import DRUMKIT_AUTH_URL from '@constants/DrumkitAuthUrl';
import { isDeployed } from '@utils/isProd';

import {
  type EmailRoutingConfig,
  assembleEmailRoutingConfig,
  emailRoutingHasAlternateTargets,
  resolveApiBaseUrl,
  resolvePortalBaseUrl,
} from './emailHostRouting';
import { getMailboxEmailAddress } from './outlookMailbox';

function normalizeApiBaseUrl(url: string): string {
  const t = url.trim();
  if (!t) {
    return t;
  }
  return t.endsWith('/') ? t : `${t}/`;
}

function normalizePortalOrigin(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

let cachedRoutingConfig: EmailRoutingConfig | null = null;

function getRoutingConfig(): EmailRoutingConfig {
  if (cachedRoutingConfig === null) {
    cachedRoutingConfig = assembleEmailRoutingConfig({
      primaryUrl: normalizeApiBaseUrl(DRUMKIT_API_URL ?? ''),
      portalUrl: normalizePortalOrigin(String(DRUMKIT_AUTH_URL ?? '')),
      definition: getEmailRoutingDefinition(),
    });
  }
  return cachedRoutingConfig;
}

// When true, axios should use resolved API base (deployed builds always;
// in dev when the active routing definition includes alternate targets).
export function isDrumkitApiBaseRoutingActive(): boolean {
  if (isDeployed()) {
    return true;
  }
  return emailRoutingHasAlternateTargets(getRoutingConfig());
}

// Resolve Drumkit API base for the given identities.
export function getResolvedDrumkitApiBaseUrl(
  mailboxEmail: string | null | undefined,
  authenticatedUserEmail: string | null | undefined
): string {
  return resolveApiBaseUrl(
    mailboxEmail,
    authenticatedUserEmail,
    getRoutingConfig()
  );
}

// Resolve portal origin for deep links (signup, relink, titlebar).
export function getResolvedDrumkitPortalBaseUrl(
  mailboxEmail: string | null | undefined,
  authenticatedUserEmail: string | null | undefined
): string {
  return resolvePortalBaseUrl(
    mailboxEmail,
    authenticatedUserEmail,
    getRoutingConfig()
  );
}

// Resolve portal origin from the current mailbox and (if available) roaming user.
export function getResolvedDrumkitPortalBaseUrlFromSession(
  authenticatedUserEmail: string | null | undefined
): string {
  return getResolvedDrumkitPortalBaseUrl(
    getMailboxEmailAddress(),
    authenticatedUserEmail
  );
}

// Set axios `defaults.baseURL` from mailbox + authenticated email when routing is active.
export function syncVestaAxiosApiBaseUrl(
  axiosInstance: Pick<AxiosInstance, 'defaults'>,
  mailboxEmail: string | null | undefined,
  authenticatedUserEmail: string | null | undefined
): string {
  if (!isDrumkitApiBaseRoutingActive()) {
    return String(axiosInstance.defaults.baseURL ?? '');
  }
  const base = resolveApiBaseUrl(
    mailboxEmail,
    authenticatedUserEmail,
    getRoutingConfig()
  );
  axiosInstance.defaults.baseURL = base;
  return base;
}

// True when a stored login pin is a different API host than login would
// choose for this mailbox today.
//
// Compares mailbox-only resolution, the same inputs as Login.tsx
// (authenticated email null). A pin kept because the authenticated email
// routes to another cluster than the mailbox stays valid.
export function isApiBaseUrlPinStale(
  pin: string | null | undefined,
  mailboxEmail: string | null | undefined
): boolean {
  const pinned = pin?.trim();
  const mailbox = mailboxEmail?.trim();
  if (!pinned || !mailbox) {
    return false;
  }
  if (!isDrumkitApiBaseRoutingActive()) {
    return false;
  }

  const expected = normalizeApiBaseUrl(
    getResolvedDrumkitApiBaseUrl(mailbox, null)
  );
  if (!expected) {
    return false;
  }

  return normalizeApiBaseUrl(pinned) !== expected;
}

// Apply routing using the current mailbox and (if available) roaming user, for interceptors.
// When `apiBaseUrlAtIssue` is set, keep axios on the cluster that issued the JWT instead of
// re-resolving from email (mailbox vs authenticated email can map to different clusters).
export function syncVestaAxiosApiBaseUrlFromSession(
  axiosInstance: Pick<AxiosInstance, 'defaults'>,
  authenticatedUserEmail: string | null | undefined,
  apiBaseUrlAtIssue?: string | null | undefined
): string {
  if (!isDrumkitApiBaseRoutingActive()) {
    return String(axiosInstance.defaults.baseURL ?? '');
  }

  const pinned = apiBaseUrlAtIssue?.trim();
  if (pinned) {
    const base = normalizeApiBaseUrl(pinned);
    axiosInstance.defaults.baseURL = base;
    return base;
  }

  return syncVestaAxiosApiBaseUrl(
    axiosInstance,
    getMailboxEmailAddress(),
    authenticatedUserEmail
  );
}
